import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ChevronLeft, Skull, Lock, Send, Trophy, Copy, BarChart3, FileDown, Gift, ChevronRight, CheckCircle2, Circle, RefreshCw, Ticket, Clock } from "lucide-react";
import { api, apiDownload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InvitesManager from "@/components/InvitesManager";
import NotifyBox from "@/components/NotifyBox";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { SurvivaPicksContent } from "@/components/SurvivaPicksContent";
import { BonusBanner } from "@/components/BonusBanner";

const SIGNS = ["1", "X", "2"];

export default function SurvivalDetail() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [t, setT] = useState(null);
  const [md, setMd] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [locked, setLocked] = useState({ locked_teams: [], lives_left: 0 });
  const [myPicks, setMyPicks] = useState({ picks: [], required: 0 });
  const [sel, setSel] = useState({}); // fixture_key -> sign
  const [summary, setSummary] = useState(null);
  const [mdList, setMdList] = useState([]);
  const [summaryMd, setSummaryMd] = useState(null);
  const [recaps, setRecaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null); // classifica: riga espansa inline
  const [openSign, setOpenSign] = useState(null); // riepilogo: "fixtureIdx-segno" aperto

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tour = await api(`/sv/tournaments/${tid}`);
      setT(tour);
      const parts = await api(`/sv/tournaments/${tid}/leaderboard`);
      setParticipants(parts);
      if (tour.joined) {
        const lk = await api(`/sv/tournaments/${tid}/locked-teams`).catch(() => ({ locked_teams: [], lives_left: 0 }));
        setLocked(lk);
      }
      try {
        const cur = await api(`/sv/tournaments/${tid}/matchdays/current`);
        setMd(cur);
        if (tour.joined) {
          const mp = await api(`/sv/tournaments/${tid}/matchdays/${cur.id}/my-picks`).catch(() => ({ picks: [], required: 0 }));
          setMyPicks(mp);
          const pre = {};
          (mp.picks || []).forEach((p) => { pre[`${p.home_team}||${p.away_team}`] = p.pick; });
          setSel(pre);
        }
      } catch { setMd(null); }
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [tid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api(`/sv/tournaments/${tid}/matchdays`).then((r) => setMdList(Array.isArray(r) ? r : (r.matchdays || []))).catch(() => {});
    api(`/sv/tournaments/${tid}/recaps`).then((r) => setRecaps(Array.isArray(r) ? r : [])).catch(() => {});
  }, [tid]);

  const required = myPicks.required || locked.lives_left || 0;
  const selCount = Object.keys(sel).length;

  const toggle = (home, away, sign) => {
    const key = `${home}||${away}`;
    setSel((prev) => {
      const next = { ...prev };
      if (next[key] === sign) { delete next[key]; return next; }
      if (!next[key] && selCount >= required) {
        toast.warning(`Puoi selezionare solo ${required} partite (1 per vita).`);
        return prev;
      }
      next[key] = sign;
      return next;
    });
  };

  const submit = async () => {
    const picks = Object.entries(sel).map(([k, pick]) => {
      const [home_team, away_team] = k.split("||");
      return { home_team, away_team, pick };
    });
    if (picks.length !== required) { toast.error(`Devi inviare esattamente ${required} pronostici.`); return; }
    try {
      await api(`/sv/tournaments/${tid}/matchdays/${md.id}/picks`, { method: "POST", body: { picks } });
      toast.success("Pronostici inviati!");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const join = async () => {
    if (!t?.invite_code) return;
    try { await api("/sv/tournaments/join", { method: "POST", body: { invite_code: t.invite_code } }); toast.success("Iscritto!"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const loadSummary = async (mdId) => {
    const id = mdId || md?.id;
    if (!id) { toast.info("Nessuna giornata disponibile"); return; }
    setSummaryMd(id);
    try { setSummary(await api(`/sv/tournaments/${tid}/matchdays/${id}/summary`)); }
    catch (e) { toast.error(e.message); }
  };

  const exportPdf = async () => {
    const sections = [{
      heading: "Classifica",
      columns: ["#", "Giocatore", "Vite", "Stato"],
      rows: participants.map((p, i) => [i + 1, p.nickname, p.lives_left, p.eliminated_at != null ? `Fuori G${p.eliminated_at}` : "In gioco"]),
    }];
    if (summary?.fixtures) {
      sections.push({
        heading: `Riepilogo giornata`,
        columns: ["Partita", "1", "X", "2"],
        rows: summary.fixtures.map((f) => [`${f.home_team} - ${f.away_team}`, f.counts?.["1"] ?? 0, f.counts?.["X"] ?? 0, f.counts?.["2"] ?? 0]),
      });
    }
    try {
      await apiDownload("/export/pdf", { title: `Survival · ${t.name}`, subtitle: `Giornata ${md?.matchday ?? t.current_matchday}`, filename: `survival_${(t.name || "torneo").replace(/\s+/g, "_")}`, sections }, "survival.pdf");
    } catch (e) { toast.error(e.message); }
  };

  if (loading || !t) return <div className="py-16 text-center text-[#94A3B8]">Caricamento...</div>;

  const alive = !myPicks && false;
  const lockedSet = new Set(locked.locked_teams || []);

  return (
    <div className="space-y-5">
      <button data-testid="svd-back" onClick={() => navigate("/survival")} className="flex items-center gap-1 text-[#94A3B8] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Tornei</button>

      <div className="rounded-xl border border-white/10 bg-[#181D22] p-4">
        <div className="text-2xl font-extrabold">{t.name}</div>
        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#94A3B8]">
          <span>Giornata <b className="text-white">{t.current_matchday}</b></span>
          <span className="text-[#EF4444] flex items-center gap-1"><Heart size={14} /> {t.players_alive}/{t.players_total} vivi</span>
          {t.joined && <span className="text-[#F59E0B] flex items-center gap-1"><Heart size={14} /> Le tue vite: {locked.lives_left}</span>}
          {t.status === "finished" && <span className="text-[#F59E0B] font-bold">Concluso</span>}
        </div>
        {t.partial && (
          <div data-testid="svd-partial-banner" className="mt-3 rounded-lg border border-[#3B82F6]/50 bg-[#3B82F6]/10 p-3 flex items-center gap-2.5">
            <Clock size={18} className="text-[#93C5FD] shrink-0" />
            <div className="text-sm">
              <b className="text-[#93C5FD]">Calcolo parziale · in corso</b>
              <span className="text-[#94A3B8] ml-1">— la classifica è provvisoria{t.pending_count > 0 ? `, mancano ${t.pending_count} risultati` : ""}.</span>
            </div>
          </div>
        )}
        {t.status === "finished" && t.winner_nickname && (
          <div data-testid="svd-winner-banner" className="mt-3 rounded-lg border border-[#00D95F] bg-[#00D95F]/10 p-3 flex items-center gap-2.5">
            <Trophy size={20} className="text-[#00D95F] shrink-0" />
            <div className="text-base">
              <span className="text-[#94A3B8]">Il vincitore è </span>
              <b className="text-[#00D95F] text-lg" data-testid="svd-winner-name">{t.winner_nickname}</b>
              <span className="ml-1">🏆</span>
            </div>
          </div>
        )}
        {(isAdmin || t.is_admin) && t.invite_code && (
          <button data-testid="svd-copy-code" onClick={() => { navigator.clipboard?.writeText(t.invite_code); toast.success("Codice copiato"); }} className="mt-3 inline-flex items-center gap-2 text-xs bg-white/10 rounded-md px-3 py-1.5">
            <Copy size={13} /> Codice invito: <b>{t.invite_code}</b>
          </button>
        )}
        {!t.joined && t.status !== "finished" && (
          <button data-testid="svd-join" onClick={join} className="mt-3 w-full bg-[#00D95F] text-[#08110A] font-bold rounded-md py-2.5">Iscriviti a questo torneo</button>
        )}
      </div>

      {t.joined && participants.some((p) => p.user_id === user?.id && !p.eliminated) && (
        <CollapsibleSection
          testid="svd-mygiocata-banner"
          icon={<Send size={18} className="text-[#F59E0B]" />}
          title="La tua giocata"
          defaultOpen
        >
          {md && (
            <div data-testid="svd-current-play" className="mb-3 rounded-xl border-2 border-[#F59E0B]/60 bg-[#F59E0B]/10 p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-extrabold text-[#F59E0B] flex items-center gap-1.5">
                  <Send size={15} /> Giornata {md.matchday} · {md.settled ? "conclusa" : md.locked ? "in corso" : "aperta"}
                </div>
                <span data-testid="svd-current-lives" className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#EF4444]/15 border border-[#EF4444]/50 text-sm font-extrabold text-[#EF4444]">
                  <Heart size={13} className="fill-[#EF4444]" /> {locked.lives_left} {locked.lives_left === 1 ? "vita" : "vite"}
                </span>
              </div>
              {(myPicks.picks && myPicks.picks.length > 0) ? (
                <div className="rounded-lg border border-white/10 bg-[#0F1216] divide-y divide-white/10">
                  {myPicks.picks.map((p, i) => {
                    const decided = p.correct === true || p.correct === false;
                    const outcome = decided ? (p.correct === false ? "ko" : "ok") : "na";
                    return (
                      <div key={i} data-testid={`svd-current-pick-${i}`} className="flex items-center gap-2 px-3 py-2">
                        <span className="flex-1 min-w-0 truncate text-sm">{p.home_team} <span className="text-[#94A3B8]">-</span> {p.away_team}</span>
                        <span className={`min-w-7 text-center px-2 py-0.5 rounded border text-sm font-extrabold ${outcome === "ok" ? "border-[#00D95F] bg-[#00D95F]/15 text-[#00D95F]" : outcome === "ko" ? "border-[#EF4444] bg-[#EF4444]/15 text-[#EF4444]" : "border-white/15 text-white"}`}>{p.pick}</span>
                        <span className="w-5 flex items-center justify-center shrink-0">
                          {outcome === "ok" && <CheckCircle2 size={16} className="text-[#00D95F]" />}
                          {outcome === "ko" && <Skull size={14} className="text-[#EF4444]" />}
                          {outcome === "na" && <Clock size={14} className="text-[#94A3B8]" />}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg bg-[#0F1216] border border-white/10 p-3 text-sm text-[#94A3B8] flex items-center gap-2">
                  {md.settled ? <><Lock size={15} /> Giornata conclusa — nessuna giocata inviata.</> : md.locked ? <><Lock size={15} /> Giornata in corso — nessuna giocata inviata.</> : <><Circle size={14} className="text-[#F59E0B]" /> Non hai ancora inviato la giocata per questa giornata.</>}
                </div>
              )}
            </div>
          )}
          <SurvivaPicksContent tid={tid} userId={user.id} />
        </CollapsibleSection>
      )}

      {t.joined && locked.locked_teams?.length > 0 && (
        <div className="rounded-lg bg-[#242A31] p-3 text-sm">
          <div className="text-xs uppercase tracking-widest text-[#94A3B8] mb-1 flex items-center gap-1"><Lock size={12} /> Squadre bloccate</div>
          <div className="flex flex-wrap gap-1.5">
            {locked.locked_teams.map((tm) => <span key={tm} className="px-2 py-0.5 rounded bg-white/10 text-xs">{tm}</span>)}
          </div>
        </div>
      )}

      {t.joined && md && !md.settled && (
        <BonusBanner game="survival" persist />
      )}

      {t.joined && md && !md.settled && (() => {
        const hasSubmitted = (myPicks?.picks?.length || 0) > 0;
        const playable = !md.locked && required > 0;
        return (
        <CollapsibleSection
          testid="svd-giocata-banner"
          icon={<Send size={18} className="text-[#F59E0B]" />}
          title={`${md.locked ? "La tua giocata" : "Fai la tua giocata"} · G${md.matchday}`}
          defaultOpen={!hasSubmitted && playable}
          right={<span className={`text-xs font-bold ${selCount === required ? "text-[#00D95F]" : "text-[#F59E0B]"}`}>{selCount}/{required} scelti</span>}
        >
          {md.locked ? (
            <div className="rounded-lg bg-[#0F1216] p-4 text-center text-[#94A3B8] flex items-center justify-center gap-2"><Lock size={16} /> Giornata chiusa: pronostici bloccati.</div>
          ) : required === 0 ? (
            <div className="rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/40 p-4 text-center text-[#EF4444] flex items-center justify-center gap-2"><Skull size={16} /> Sei stato eliminato.</div>
          ) : (
            <>
              <p className="text-xs text-[#94A3B8] mb-2">Scegli {required} partite diverse (una per vita) e il segno 1/X/2.</p>
              <div className="rounded-xl border border-white/10 bg-[#0F1216] divide-y divide-white/10">
                {(md.fixtures || []).map((f, i) => {
                  const key = `${f.home_team}||${f.away_team}`;
                  const cur = sel[key];
                  const postponed = f.postponed_before;
                  return (
                    <div key={i} data-testid={`svd-fixture-${i}`} className={`px-3 py-3 ${postponed ? "opacity-40" : ""}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm flex-1 min-w-0">
                          <span className={lockedSet.has(f.home_team) ? "text-[#F59E0B]" : ""}>{f.home_team}</span>
                          <span className="text-[#94A3B8] mx-1">-</span>
                          <span className={lockedSet.has(f.away_team) ? "text-[#F59E0B]" : ""}>{f.away_team}</span>
                        </div>
                        <div className="flex gap-1">
                          {SIGNS.map((s) => (
                            <button key={s} data-testid={`svd-sign-${i}-${s}`} disabled={postponed} onClick={() => toggle(f.home_team, f.away_team, s)}
                              className={`h-9 w-9 rounded-md border font-bold transition-colors ${cur === s ? "bg-[#00D95F] border-[#00D95F] text-[#08110A]" : "bg-[#181D22] border-white/15 text-white hover:border-white/40"}`}>{s}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button data-testid="svd-submit" onClick={submit} disabled={selCount !== required} className="mt-3 w-full bg-[#F59E0B] text-[#1A1000] font-extrabold rounded-md py-3 flex items-center justify-center gap-2 disabled:opacity-50">
                <Send size={18} /> Invia {required} pronostici
              </button>
            </>
          )}
        </CollapsibleSection>
        );
      })()}

      {/* Storico / Riepilogo giornata — banner a tendina */}
      <CollapsibleSection
        testid="svd-storico-banner"
        icon={<BarChart3 size={18} className="text-[#F59E0B]" />}
        title="Storico / Riepilogo giornata"
      >
        {mdList.length > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-[#94A3B8]">Giornata</span>
            <select data-testid="svd-md-select" value={summaryMd || md?.id || ""} onChange={(e) => loadSummary(e.target.value)} className="bg-[#0F1216] border border-white/15 rounded-md px-2 py-1 text-xs">
              <option value="">Giornata…</option>
              {mdList.map((m) => <option key={m.id} value={m.id}>G{m.matchday ?? m.matchday_number} {m.status === "settled" ? "✓" : ""}</option>)}
            </select>
          </div>
        )}
        <button data-testid="svd-summary-btn" onClick={() => loadSummary()} className="w-full text-sm font-bold text-[#F59E0B] border border-[#F59E0B]/30 rounded-md py-2">
          Mostra riepilogo {md ? `giornata ${md.matchday ?? t.current_matchday}` : ""}
        </button>
        <button data-testid="svd-export-pdf" onClick={exportPdf} className="mt-2 w-full flex items-center justify-center gap-2 text-sm font-bold text-[#00D95F] border border-[#00D95F]/30 rounded-md py-2">
          <FileDown size={16} /> Esporta PDF (riepilogo + classifica)
        </button>
        {summary && (
          <div className="mt-3 space-y-3">
            <div className={`rounded-lg border p-3 flex items-start gap-2 text-xs ${summary.counts_hidden ? "border-white/10 bg-[#0F1216] text-[#94A3B8]" : "border-[#F59E0B]/40 bg-[#F59E0B]/5 text-[#F8C471]"}`}>
              <BarChart3 size={15} className="shrink-0 mt-0.5" />
              <span>{summary.counts_hidden ? "Conteggi nascosti per privacy (pochi superstiti) fino al calcio d'inizio." : "Giornata visibile: tocca un segno per vedere chi l'ha giocato."}</span>
            </div>
            {(summary.fixtures || []).map((f, i) => {
              const c1 = f.counts?.["1"] ?? 0, cx = f.counts?.["X"] ?? 0, c2 = f.counts?.["2"] ?? 0;
              const total = c1 + cx + c2;
              const winner = c1 >= cx && c1 >= c2 ? "1" : cx >= c2 ? "X" : "2";
              const openThis = openSign && openSign.startsWith(`${i}|`) ? openSign.slice(String(i).length + 1) : null;
              return (
                <div key={i} className="rounded-xl border border-white/10 bg-[#0F1216] p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-extrabold flex-1 truncate">{f.home_team}</span>
                    <span className="text-xs text-[#94A3B8]">vs</span>
                    <span className="font-extrabold flex-1 text-right truncate">{f.away_team}</span>
                  </div>
                  {summary.counts_hidden ? (
                    <div className="flex items-center gap-1.5 text-xs text-[#94A3B8] italic"><BarChart3 size={13} /> conteggi nascosti</div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        {["1", "X", "2"].map((s) => {
                          const cnt = f.counts?.[s] ?? 0;
                          const isWinner = total > 0 && cnt > 0 && s === winner;
                          const players = (f.picks || []).filter((pk) => pk.pick === s);
                          const canOpen = players.length > 0;
                          const key = `${i}|${s}`;
                          return (
                            <button key={s} data-testid={`svd-sum-${i}-${s}`} disabled={!canOpen}
                              onClick={() => setOpenSign((o) => (o === key ? null : key))}
                              className={`flex-1 rounded-lg border py-2 flex flex-col items-center gap-0.5 transition-colors ${isWinner ? "bg-[#EF4444]/15 border-[#EF4444]" : "bg-[#181D22] border-white/10"} ${canOpen ? "cursor-pointer hover:brightness-125" : "cursor-default"} ${openThis === s ? "ring-2 ring-[#F59E0B]" : ""}`}>
                              <div className={`text-sm font-extrabold ${isWinner ? "text-[#EF4444]" : "text-white"}`}>{s}</div>
                              <div className="flex items-center gap-1">
                                <span className={`font-extrabold ${isWinner ? "text-[#EF4444]" : "text-[#94A3B8]"}`}>{cnt}</span>
                                <Heart size={11} className={isWinner ? "text-[#EF4444] fill-[#EF4444]" : "text-[#64748B]"} />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {openThis && (
                        <div data-testid={`svd-sign-players-${i}-${openThis}`} className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-2.5">
                          <div className="text-xs text-[#94A3B8] mb-1.5">Hanno giocato <b className="text-[#F59E0B]">{openThis}</b>:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(f.picks || []).filter((pk) => pk.pick === openThis).map((pk, pi) => (
                              <span key={pi} data-testid={`svd-sign-player-${i}-${openThis}-${pi}`} className={`text-xs px-2 py-0.5 rounded border ${pk.correct === false ? "bg-[#EF4444]/10 border-[#EF4444]/40 text-[#EF4444]" : pk.correct === true ? "bg-[#00D95F]/10 border-[#00D95F]/40 text-[#00D95F]" : "bg-white/10 border-white/10 text-white"}`}>{pk.nickname}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      {/* Classifica — banner a tendina */}
      <CollapsibleSection
        testid="svd-classifica-banner"
        icon={<Trophy size={18} className="text-[#F59E0B]" />}
        title="Classifica"
        defaultOpen
      >
        <p className="text-xs text-[#94A3B8] mb-2">Tocca un giocatore per vedere la sua giocata.</p>
        <div className="rounded-xl border border-white/10 bg-[#0F1216] divide-y divide-white/10">
          {participants.map((p) => {
            const out = p.eliminated;
            const isOpen = expandedUser === p.user_id;
            return (
              <div key={p.user_id}>
                <button data-testid={`sv-lb-row-${p.user_id}`} onClick={() => setExpandedUser(isOpen ? null : p.user_id)} className={`w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors ${out ? "opacity-60" : ""}`}>
                  <span className="w-8 text-[#EF4444] font-extrabold shrink-0">#{p.rank}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-bold truncate ${out ? "line-through" : ""}`}>{p.nickname}</div>
                    {out ? (
                      <div className="flex items-center gap-1 text-xs text-[#94A3B8]"><Skull size={12} /> {p.eliminated_matchday ? `Eliminato · G${p.eliminated_matchday}` : "Eliminato"}</div>
                    ) : (
                      <div className={`flex items-center gap-1 text-xs ${p.has_submitted_current ? "text-[#00D95F]" : "text-[#F59E0B]"}`}>
                        {p.has_submitted_current ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                        {p.has_submitted_current ? "Giocata effettuata" : "In attesa di giocata"}
                      </div>
                    )}
                    {!out && p.bonus_active && (
                      <div data-testid={`sv-lb-bonus-status-${p.user_id}`} className={`flex items-center gap-1 text-xs mt-0.5 ${p.has_bonus_current ? "text-[#10B981]" : "text-[#94A3B8]"}`}>
                        <Gift size={12} /> {p.has_bonus_current ? "Bonus giocato" : "Bonus in attesa"}
                      </div>
                    )}
                    {!out && Array.isArray(p.locked_teams) && p.locked_teams.length > 0 && (
                      <div data-testid={`sv-lb-blocked-${p.user_id}`} className="flex flex-wrap items-center gap-1 mt-1.5">
                        <Lock size={11} className="text-[#94A3B8] shrink-0" />
                        {p.locked_teams.map((team) => (
                          <span
                            key={team}
                            data-testid={`sv-lb-blocked-chip-${p.user_id}-${team}`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/30 text-[10px] font-bold text-[#F87171] leading-none"
                          >
                            {team}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-stretch gap-1 shrink-0">
                    <span data-testid={`sv-lb-prelives-${p.user_id}`} className="flex items-center justify-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-xs font-bold text-[#94A3B8]"><Heart size={11} /> {p.pick_lives}</span>
                    <span data-testid={`sv-lb-bonus-${p.user_id}`} className="flex items-center justify-center gap-1 px-2 py-0.5 rounded-md bg-[#F59E0B]/10 border border-[#F59E0B]/50 text-xs font-bold text-[#F59E0B]"><Gift size={11} /> +{p.bonus_wins ?? 0}</span>
                    <span data-testid={`sv-lb-lives-${p.user_id}`} className="flex items-center justify-center gap-1 px-2 py-0.5 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/50 text-xs font-extrabold text-[#EF4444]"><Heart size={11} className="fill-[#EF4444]" /> {p.lives_left}</span>
                  </div>
                  <ChevronRight size={18} className={`text-[#94A3B8] shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-3 pb-3">
                    <SurvivaPicksContent tid={tid} userId={p.user_id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleSection>

      {/* Riassunti giornate passate — in fondo */}
      {Array.isArray(recaps) && recaps.length > 0 && (
        <CollapsibleSection
          testid="svd-recaps-banner"
          icon={<BarChart3 size={18} className="text-[#F59E0B]" />}
          title="Riassunti giornate passate"
        >
          <div className="space-y-2" data-testid="svd-recaps">
            {recaps.slice().reverse().map((r) => (
              r.tie_break ? (
                <div key={r.matchday} data-testid={`svd-recap-${r.matchday}`} className="rounded-lg border border-[#F59E0B] bg-[#F59E0B]/10 p-3 flex items-start gap-2.5">
                  <RefreshCw size={18} className="text-[#F59E0B] shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-extrabold text-[#F59E0B] uppercase tracking-wide">Tie-break · Resurrezione G{r.matchday}</div>
                    <div className="text-[#F8C471] mt-0.5">Tutti i partecipanti sono morti: si rigioca con le stesse condizioni della giornata precedente.</div>
                  </div>
                </div>
              ) : (
                <div key={r.matchday} data-testid={`svd-recap-${r.matchday}`} className="rounded-lg border border-white/10 bg-[#0F1216] p-3 flex items-start gap-2.5">
                  <BarChart3 size={16} className="text-[#94A3B8] shrink-0 mt-0.5" />
                  <div className="text-sm flex-1 min-w-0">
                    <div className="font-extrabold text-white">Giornata {r.matchday} conclusa</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <span className="flex items-center gap-1 text-[#EF4444]"><Skull size={13} /> {r.eliminated_count} {r.eliminated_count === 1 ? "eliminato" : "eliminati"}</span>
                      <span className="flex items-center gap-1 text-[#F59E0B]"><Gift size={13} /> {r.bonus_count} bonus</span>
                    </div>
                    {r.bonus_count > 0 && (
                      <div className="mt-1 text-xs text-[#F8C471]">Bonus vita: <b>{r.bonus_names.join(", ")}</b></div>
                    )}
                    {r.eliminated_count > 0 && r.eliminated_names.length > 0 && (
                      <div className="mt-0.5 text-xs text-[#94A3B8]">Eliminati: {r.eliminated_names.join(", ")}</div>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Admin: Gestione inviti — banner a tendina */}
      {(isAdmin || t.is_admin) && (
        <CollapsibleSection
          testid="svd-inviti-banner"
          icon={<Ticket size={18} className="text-[#F59E0B]" />}
          title="Gestione inviti"
        >
          <InvitesManager basePath={`/sv/tournaments/${tid}`} bare />
        </CollapsibleSection>
      )}

      {(isAdmin || t.is_admin) && <NotifyBox userIds={(participants || []).map((e) => e.user_id)} url={`/survival/${tid}`} />}
    </div>
  );
}
